import { useEffect, useRef } from "react";

/**
 * Publish an element's live height as a CSS custom property on <html>.
 *
 * The bottom sheet has to stop just below the top bar so that row stays visible
 * behind the backdrop — and the top bar's height is not a constant: a long
 * patient name wraps it, and the browser's own font settings move it. A
 * hardcoded offset would overlap the bar on one device and leave a gap on
 * another.
 *
 * Exactly the pattern PatientDetails.js:548-563 already uses for --ph-sticky-h,
 * including the ResizeObserver feature test — that API is missing on browsers
 * this app still supports, and the initial measurement alone is right for every
 * case except a reflow.
 *
 * @param {string} varName e.g. "--zym-row1-h"
 * @returns {object} ref to attach to the element being measured
 */
const useCssVarHeight = (varName, deps = []) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      el.ownerDocument.documentElement.style.setProperty(varName, `${h}px`);
    };
    publish();

    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varName, ...deps]);

  return ref;
};

export default useCssVarHeight;
